<template>
  <!-- container -->
  <div class="container mx-auto md:mx-0 p-8 md:p-12 md:w-2/3 h-screen">
    <!-- Sign out  -->
    <div class="flex justify-end">
      <div class="flex flex-row text-gray-800 cursor-pointer" @click="signOut">
        <div class="md:visible invisible text-gray-600">
          {{ $t("buttons.signOut") }}
        </div>
        <font-awesome-icon color="grey" icon="sign-out-alt" class="ml-3 mt-1" />
      </div>
    </div>

    <!-- form section -->
    <div :class="formSectionClasses">
      <!-- logo -->
      <div
        class="flex item-center justify-center md:mb-12 mb-6 w-full flex-col md:flex-row"
      >
        <div class="md:w-1/3 md:w-56 w-full flex justify-center">
          <div class="flex flex-col md:mt-10 mt-0">
            <img src="@/assets/icons/logo.svg" />

            <div class="flex flex-row relative justify-center md:w-56">
              <!-- Info icon -->
              <rele-info-tooltip
                icon="info"
                @mouseover="onIconHover"
                @mouseleave="onIconLeave"
                :tooltip-html="$t('signup.info.tooltips.createUser')"
                class="absolute right-0 md:mr-4 mr-12"
              />

              <!-- SIGN UP text -->
              <div
                class="mt-5 text-r-primary text-2xl text-center tracking-widest"
              >
                {{ $t("signup.signup") }}
              </div>
            </div>
          </div>
        </div>

        <!-- steps -->
        <div class="flex w-2/3 w-full md:ml-10 ml-0 mt-8 md:mt-0">
          <rele-auth-steps :current-step="1" style="z-index: -10" />
        </div>
      </div>

      <!-- description -->
      <p v-html="$t('signup.provideNumberDescription')" class="mb-6" />

      <!-- first name input -->
      <r-text-field
        id="form-first-name"
        v-model="providedFirstName"
        :label="$t('forms.firstname')"
        v-if="!displayName"
        validation="firstName"
        class="md:w-80 w-auto"
      />

      <!-- last name input -->
      <r-text-field
        id="form-last-name"
        v-model="providedLastName"
        :label="$t('forms.lastname')"
        validation="lastName"
        v-if="!displayName"
        class="md:w-80 w-auto mb-4"
      />

      <!-- form -->
      <div v-if="isCountryCodeLoading" class="w-80 flex justify-center">
        <r-loading />
      </div>
      <vue-phone-number-input
        v-else
        id="form-mobile-input"
        class="outline-none md:w-80 w-auto"
        :style="stylePhoneInput"
        :default-country-code="countryCode"
        v-model="mobileNumber"
        @update="handleMobileUpdate"
      />

      <!-- submit update -->
      <r-button
        id="form-submit-next-button"
        class="mt-16 md:mt-8 mb-20"
        :loading="loading"
        :disabled="isFormDisabled"
        @click="createUser"
      >
        {{ $t("buttons.next") }}
      </r-button>
    </div>

    <!-- right side image section -->
    <div
      :style="{ backgroundImage: `url(${bgUrl})` }"
      class="hidden md:block h-screen w-1/3 absolute top-0 right-0"
    />
  </div>
</template>

<script>
import axios from "axios"
import iso from "iso-639-1"
import { mapGetters } from "vuex"
import firebase from "firebase/app"
import componentApi from "@/api/components"
import hubspotAPI from "@/api/hubspot"
import bgUrl from "@/assets/images/background-img.png"
import { validateFirstName, validateLastName } from "../../utils"

/**
 * This page check if the user
 * is authenticated and check if all the
 * required field are aviliable in the store
 */
export default {
  // component state
  data() {
    return {
      // background img url
      bgUrl,

      // supported bot languages
      supportedBotLang: {
        EN: 1,
        ES: 2,
        FR: 3,
        GR: 4,
        INHERIT: 5
      },

      // loading
      loading: false,

      // country code loading
      isCountryCodeLoading: true,

      // the mobile number from the user
      mobileNumber: "",
      mobileNumberFormatted: "",
      countryCode: "US",
      isMobileValid: false,

      // name values
      providedFirstName: "",
      providedLastName: "",

      // infoInfoActive
      infoInfoActive: false,

      // sign out loading flag
      signoutLoading: false
    }
  },

  // calculated props
  computed: {
    ...mapGetters({
      user: "auth/getUser",
      locale: "auth/getLocale",
      lastName: "auth/getLastName",
      firstName: "auth/getFirstName",
      isNewUser: "auth/getIsNewUser",
      displayName: "auth/getDisplayName",
      userFsId: "auth/getUserFsId",
      hd: "auth/getHd",
      jwt: "auth/getIdToken",
      contactId: "hubspot/getReleCrmContactId"
    }),

    /**
     * Returns the email address from the user
     */
    email() {
      return this.user.email
    },

    /**
     * Is form disabled
     */
    isFormDisabled() {
      if (this.displayName) {
        return !this.isMobileValid
      } else {
        return (
          !validateFirstName(this.providedFirstName) ||
          !validateLastName(this.providedLastName) ||
          !this.isMobileValid ||
          !this.providedLastName ||
          !this.providedFirstName
        )
      }
    },

    /**
     * Formatted first name
     */
    formattedFirstName() {
      let firstNameFromDisplay = (this.displayName || "").split(" ")
      firstNameFromDisplay =
        firstNameFromDisplay.length > 1 ? firstNameFromDisplay[0] : ""

      return this.firstName || firstNameFromDisplay || this.providedFirstName
    },

    /**
     * Formatted last name
     */
    formattedLastName() {
      let lastNameFromDisplay = (this.displayName || "").split(" ")
      lastNameFromDisplay =
        lastNameFromDisplay.length > 1 ? lastNameFromDisplay[1] : ""

      return this.lastName || lastNameFromDisplay || this.providedLastName
    },

    /**
     * stylePhoneInput returns the phone input style
     */
    stylePhoneInput() {
      if (this.infoInfoActive) {
        return { "z-index": -10 }
      }

      return { "z-index": 0 }
    },

    /**
     * Form section classes
     */
    formSectionClasses() {
      let baseClasses = {
        flex: true,
        "flex-col": true,
        "item-center": true,
        "md:items-start": true,
        "justify-center": true,
        "md:justify-start": true,
        "h-full": true,
        "md:pr-64": true
      }

      if (!this.displayName) {
        baseClasses = {
          ...baseClasses,
          "mt-20": true,
          "md:mt-0": true
        }
      }

      return baseClasses
    },

    /**
     * Returns user prefrred language
     */
    userPreferredLanguage() {
      return (
        this.formattedLocale ||
        (navigator.language || "").toLowerCase() ||
        navigator.userLanguage ||
        "EN"
      )
    },

    /**
     * Formatted locale - from lang code to language
     */
    formattedLocale() {
      if (this.locale.includes("-")) {
        let code = this.locale.split("-")[0]
        if (iso.validate(code)) {
          return iso.getName(code)
        }
      } else if (iso.validate(this.locale)) {
        return iso.getName(this.locale)
      } else if (this.locale.includes(";")) {
        let lang = this.locale.split(";")[0]
        let code = iso.getCode(lang)
        if (code) {
          return lang
        }
      } else if (iso.getCode(this.locale)) {
        return this.locale
      }
      return "English"
    }
  },

  /**
   * On mount check for missing attributes
   */
  async mounted() {
    // redirect user if connected
    if (!this.isNewUser && this.userFsId) {
      this.$router.replace("/")
      return
    }

    // pull country code from ip
    await this.setInfoFromIP()
  },

  // handle notification
  notifications: {
    notifyError: {
      type: "error"
    }
  },

  // watch props
  watch: {
    // monitor the user access to bind data
    hd: {
      immediate: true,
      async handler() {
        const jwt = await firebase.auth().currentUser.getIdToken()
        const orgs = await componentApi.orgs(jwt).list()
        const org = orgs.find((org) => org.name === this.hd)
        // if user is first
        if (!org && this.isNewUser) {
          // create user on RELE's CRM STEP1
          if (process.env.VUE_APP_FIRE_ENV === "production") {
            await this.recordToReleCrm()
          }
        }
      }
    }
  },

  // component functions
  methods: {
    /**
     * Handle mobile update. Extract formatted value
     */
    handleMobileUpdate(v) {
      this.mobileNumberFormatted = v.formattedNumber
      this.countryCode = v.countryCode
      this.isMobileValid = v.isValid
    },

    /**
     * Check all required attributes are provided
     * by google auth
     */
    missingAttributes() {
      return this.user && this.email && this.lastName && this.firstName
    },

    /**
     * Set country code by IP address
     */
    async setInfoFromIP() {
      // resolve promise and get data from ip
      try {
        const loc = await axios.get("https://json.geoiplookup.io/")
        this.countryCode = loc.data?.country_code || "US"
      } catch (e) {
        console.error(e)
        this.countryCode = "US"
      }

      // set loader to false
      this.isCountryCodeLoading = false
    },

    /**
     * Sign out user
     */
    async signOut() {
      // toggle loading
      this.signoutLoading = true

      // define auth and current user, and jwt
      const fsAuth = firebase.auth()
      const user = fsAuth.currentUser
      const jwt = await user.getIdToken()

      try {
        // delete fb-auth
        await componentApi.users(jwt).deleteUserWithAuth(user.uid)

        // reload page
        location.reload()
      } catch (e) {
        this.signoutLoading = false
        this.notifyError({
          title: this.$t("errors.unableToSignOut"),
          message: "Unable to sign out user"
        })
      }
    },

    /**
     * Check if user have active integrations
     */
    userHaveIntegration(user, org) {
      if (org.hubspot?.api_key) return true
      else if ((user.hubspot || {})[org.id]) return true
      else if ((user.salesforce || {})[org.id]) return true
      else if ((user.mailing || {})[org.id]) return true
      else return false
    },

    /**
     * Create user in firestore
     */
    async createUser() {
      this.loading = true

      if (this.missingAttributes && this.mobileNumberFormatted) {
        // set mobile
        this.$store.commit(
          "auth/setMobile",
          this.mobileNumberFormatted.slice(1)
        )

        try {
          // create new user
          const user = {
            id: this.user.uid,
            emails: [this.email],
            lastName: this.formattedLastName,
            firstName: this.formattedFirstName,
            mobiles: [this.mobileNumberFormatted.slice(1)],
            mobiles_country_code: this.countryCode || "US",
            botLang: this.supportedBotLang["INHERIT"] || 1,
            auth: {
              accessLevel: 30
            }
          }

          let org = {}
          if (this.hd) {
            const jwt = await firebase.auth().currentUser.getIdToken()
            const orgs = await componentApi.orgs(jwt).list()
            org = orgs.find((org) => org.name === this.hd)
            if (org) {
              user.orgs = [org.id]
            }
          }

          // create user
          await this.$store.dispatch("users/add", user)

          // update claims
          await firebase.auth().currentUser.getIdToken(true)
          await this.$store.dispatch(
            "auth/processUser",
            firebase.auth().currentUser
          )

          if ((user.orgs || []).length) {
            if (this.userHaveIntegration(user, org)) {
              console.log("new user redirect to root")
              this.$router.replace("/")
            } else {
              console.log("new user redirect to choose integration")
              this.$router.replace("/auth/first-integration")
            }
          } else {
            //  Update rele.ai crm STEP2
            if (process.env.VUE_APP_FIRE_ENV === "production") {
              this.updateRecordReleCrm()
            }

            console.log("new user redirect to gen org")
            this.$router.push("/auth/gen-org")
          }
        } catch (e) {
          this.loading = false
          console.error("unable to create user", e)
          // notify the user about error while creating user
          this.notifyError({
            title: this.$t("errors.unableToCreateUser"),
            message: e.message
          })
        }
      } else {
        // notify the user about missing values
        this.notifyError({
          title: this.$t("errors.missingRequiredField")
        })
      }
    },

    /**
     * Record to RELE.AI CRM
     */
    async recordToReleCrm() {
      const api = hubspotAPI("NONE_API", this.jwt)
      const res = await api.notify("record_contact_rele_crm", {
        contact: {
          properties: [
            { property: "firstname", value: this.firstName },
            { property: "lastname", value: this.lastName },
            { property: "email", value: this.user.email },
            { property: "product_lifecycle_stage", value: "Opportunity" },
            { property: "language", value: this.userPreferredLanguage }
          ]
        }
      })

      // pull contact id
      const id = ((res || {}).id || "").toString()

      // set to store
      this.$store.commit("hubspot/setReleCrmContactId", id)
    },

    /**
     * Create user on RELE.AI crm if not exists
     */
    async updateRecordReleCrm() {
      const api = hubspotAPI("NONE_API", this.jwt)
      // get conatct id
      const contactId = await this.getContactId()

      if (contactId) {
        // define base contact data to update
        let contactBase = {
          properties: [
            { property: "phone", value: this.mobileNumberFormatted },
            { property: "country", value: this.countryCode }
          ]
        }

        // if sign in with email and password,
        // update also first name and last name
        if (this.providedFirstName && this.providedLastName) {
          contactBase = {
            properties: [
              ...contactBase.properties,
              { property: "firstname", value: this.providedFirstName },
              { property: "lastname", value: this.providedLastName }
            ]
          }
        }

        // update contact
        try {
          await api.notify("update_contact_rele_crm", {
            id: contactId.toString(),
            contact: contactBase
          })
        } catch (e) {
          console.error(e)
          console.error(
            "Cannot set user to RELE.AI CRM. Maybe user already exsits on RELE's CRM"
          )
        }
      }
    },

    /**
     * Get contact ID
     */
    async getContactId() {
      return this.contactId ? this.contactId : await this.searchContact()
    },

    /**
     * Search contact on RELE CRM
     */
    async searchContact() {
      const api = hubspotAPI("NONE_API", this.jwt)

      try {
        const searchContactId = await api.notify("search_contact_rele_crm", {
          contact: {
            properties: [{ property: "email", value: this.user.email }]
          }
        })

        return searchContactId.id || ""
      } catch (e) {
        console.error("Cannot find any contact match on RELE.AI CRM")
      }
    },

    /**
     * On info icon hover
     */
    onIconHover() {
      this.infoInfoActive = true
    },

    /**
     * On info icon leave
     */
    onIconLeave() {
      this.infoInfoActive = false
    }
  }
}
</script>
